import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { onboardingService } from '@/features/onboarding/services/onboardingService';
import { OnboardingWizard } from '@/features/onboarding/components/OnboardingWizard';
import { OnboardingErrorScreen } from '@/features/onboarding/components/OnboardingErrorScreen';
import styles from '@/features/onboarding/components/Onboarding.module.css';
import { t } from '@/i18n';
import { APP_VERSION, isQaFrontendHost } from '@/config/version';

export function OnboardingInvitePage() {
	const [searchParams] = useSearchParams();
	const reference = searchParams.get('reference') ?? '';
	const [isValidating, setIsValidating] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isValid, setIsValid] = useState(false);
	const [user, setUser] = useState<any>(null);
	const hasValidated = useRef(false);

	// Marcador de versión SOLO en QA frontend: confirma bundle desplegado en el CDN.
	useEffect(() => {
		if (isQaFrontendHost()) {
			console.log(`[chikara-frontend v${APP_VERSION}] onboarding-invite loaded`);
		}
	}, []);

	useEffect(() => {
		if (!reference) {
			setError(t('onboarding.error.invalidToken'));
			setIsValidating(false);
			return;
		}

		if (hasValidated.current) return;
		hasValidated.current = true;

		// Solo VALIDAMOS la invitación en la carga: read-only/idempotente, NO
		// mintea ni consume el token. La usamos para confirmar que el token es
		// válido y guardar los datos del usuario (uuid/email/status) que pinta el
		// wizard. El accept que mintea el password_token ocurre recién al confirmar
		// la contraseña (ver useSetPassword), nunca aquí ni cacheado.
		const validate = async () => {
			try {
				const result = await onboardingService.validateInvite(reference);
				const data = result?.data ?? null;
				if (data?.invitation_valid === false) {
					setError(t('onboarding.error.subtitle'));
					return;
				}
				setUser(data);
				setIsValid(true);
				setError(null);
			} catch (err: any) {
				setError(t('onboarding.error.subtitle'));
			} finally {
				setIsValidating(false);
			}
		};

		validate();
	}, [reference]);

	if (isValidating) {
		return (
			<div className={styles.loadingContainer}>
				<div className={styles.spinner} />
				<p>{t('onboarding.error.validating')}</p>
			</div>
		);
	}

	if (error || !reference) {
		return <OnboardingErrorScreen message={error} />;
	}

	return (
		<div className={styles.pageContainer}>
			{isValid && (
				<OnboardingWizard reference={reference} user={user} />
			)}
		</div>
	);
}
